import React, { useEffect, useRef } from "react";
import "./VideoFeed.css";
import InstagramIcon from "@mui/icons-material/Instagram";
import AliceCarousel from "react-alice-carousel";
import { Link } from "react-router-dom";
import useSiteSettings from "../../../hooks/useSiteSettings";

const VideoFeed = ({ section }) => {
  const carouselRef = useRef(null);
  const trackRef = useRef(null);
  const wheelLock = useRef(0);
  const s = useSiteSettings();

  // Admin-managed Instagram link (Settings → Contact & Social) — no fallback.
  const instaUrl = s.instagramUrl || "";
  const handle = (instaUrl.match(/instagram\.com\/@?([^/?]+)/i)?.[1] || "")
    .replace(/\/$/, "");

  // Admin-managed reels only (video URL + click link) — no hardcoded fallback.
  const reels = (s.reels || []).map((r) => ({
    video: r.video,
    link: r.link || instaUrl,
  }));

  // Mouse-wheel over the reels advances the carousel (throttled).
  useEffect(() => {
    const el = trackRef.current;
    if (!el) return undefined;
    const onWheel = (e) => {
      const delta =
        Math.abs(e.deltaY) >= Math.abs(e.deltaX) ? e.deltaY : e.deltaX;
      if (Math.abs(delta) < 4) return;
      e.preventDefault();
      const now = Date.now();
      if (now - wheelLock.current < 450) return;
      wheelLock.current = now;
      if (delta > 0) carouselRef.current?.slideNext();
      else carouselRef.current?.slidePrev();
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [reels.length]);

  // Nothing to show until the admin adds reels.
  if (!reels.length) return null;

  const responsive = {
    0: { items: 2, itemsFit: "contain" },
    568: { items: 3, itemsFit: "contain" },
    1024: { items: 4, itemsFit: "contain" },
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-6 text-center lg:px-8">
      {/* header — only shown when an Instagram link is configured */}
      {instaUrl && (
        <a
          href={instaUrl}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.22em] text-[#b28585] transition-colors hover:text-[#5e3e3e]"
        >
          <InstagramIcon sx={{ fontSize: 16 }} />{" "}
          {handle ? `Straight from @${handle}` : "Follow us on Instagram"}
        </a>
      )}
      {section && (
        <h2 className="mt-2 font-display text-3xl font-bold text-[#511d00] sm:text-4xl">
          {section}
        </h2>
      )}
      <div className="mx-auto mt-3 h-[3px] w-20 rounded-full bg-gradient-to-r from-[#b28585] to-[#b2a885]" />

      {/* reels */}
      <div className="relative mt-8" ref={trackRef}>
        <AliceCarousel
          disableButtonsControls
          disableDotsControls
          mouseTracking
          items={reels.map((reel, i) => {
            const link = reel.link || instaUrl; // custom link, else the IG link
            // Same tab, not a new one. An in-app path ("/men/sherwani/...")
            // goes through the router so it's a client-side transition — an
            // <a href> would trigger a full page reload and throw away the
            // loaded app. Anything else (e.g. an Instagram URL) is a plain
            // same-tab link.
            const isInternal = link.startsWith("/");
            const Wrapper = link ? (isInternal ? Link : "a") : "div";
            const wrapperProps = !link
              ? {}
              : isInternal
              ? { to: link }
              : { href: link, rel: "noreferrer" };
            return (
              <Wrapper
                key={i}
                {...wrapperProps}
                className="group relative mx-2 block overflow-hidden rounded-2xl border border-[#eadcc0] bg-[#2a1810] shadow-md transition-transform duration-300 hover:-translate-y-1"
              >
                <div className="aspect-[9/16] w-full">
                  <video
                    src={reel.video}
                    muted
                    loop
                    autoPlay
                    playsInline
                    className="h-full w-full object-cover"
                  />
                </div>
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 transition-opacity group-hover:opacity-100" />
                {instaUrl && (
                  <span className="absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-full bg-white/85 text-[#b28585] shadow">
                    <InstagramIcon sx={{ fontSize: 18 }} />
                  </span>
                )}
              </Wrapper>
            );
          })}
          responsive={responsive}
          ref={carouselRef}
          autoPlay
          autoPlayInterval={2500}
          autoPlayDirection="ltr"
          infinite
          animationEasingFunction="ease"
          animationDuration={800}
        />
      </div>
    </div>
  );
};

export default VideoFeed;
